import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Tag, Search, Plus, ChevronDown } from 'lucide-react';
import usePopoverPosition from '../../hooks/usePopoverPosition';
import OsScroll from '../ui/OsScroll';
import { labelColor } from '../../extensions/bundled/task-manager/boardLabels';

/**
 * The task window's Labels chip (owner, 06.10.2026): a dropdown with the
 * board's labels as checkboxes — ticking one adds it to the task, the menu
 * stays open for the next — and a search field that also creates: type a name
 * that is not there and "Create “…”" (or Enter) adds it to the board and ticks
 * it for this task. Built from the app picker's own parts (auth-picker menu,
 * search, rows, checkbox-brand), so it looks like every other dropdown.
 *
 * @param {{ palette: {id,name,color}[], held: string[], onToggle: (id) => void, onCreate: (name) => void }} props
 */
/** Stable (a new function each render would make the popover hook re-measure forever). */
const menuWidth = (r) => Math.max(r.width, 240);

export default function TaskLabelPicker({ palette, held, onToggle, onCreate }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const triggerRef = useRef(null);
  const searchRef = useRef(null);
  const pos = usePopoverPosition({ anchorRef: triggerRef, open, width: menuWidth, placement: 'auto', deps: [palette.length] });

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
  }, []);
  useEffect(() => {
    if (!open) return undefined;
    searchRef.current?.focus();
    // On window, capture phase: before the task window's own Escape (document, capture).
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopImmediatePropagation(); // the menu, not the task window
        e.stopPropagation();
        close();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, close]);

  const q = query.trim();
  const shown = q ? palette.filter((l) => l.name.toLowerCase().includes(q.toLowerCase())) : palette;
  const exact = q ? palette.find((l) => l.name.toLowerCase() === q.toLowerCase()) : null;
  const create = () => {
    if (!q || exact) return;
    onCreate(q);
    setQuery('');
  };
  const onSearchKey = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (exact) onToggle(exact.id);
    else if (q) create();
  };

  const menu = open
    ? createPortal(
        <>
          <div
            className="auth-picker-backdrop"
            aria-hidden
            onMouseDown={(e) => {
              e.preventDefault();
              close();
            }}
          />
          <div
            className="auth-picker__menu task-label-menu"
            role="listbox"
            aria-multiselectable="true"
            aria-label="Labels"
            style={{
              position: 'fixed',
              ...(pos.dir === 'up' ? { bottom: pos.bottom } : { top: pos.top }),
              left: pos.left,
              minWidth: pos.width,
              maxHeight: pos.maxHeight,
              zIndex: 10040,
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="auth-picker__search">
              <Search size={12} className="auth-picker__search-icon" aria-hidden />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onSearchKey}
                placeholder="Find or create a label…"
                className="auth-picker__search-input"
                aria-label="Find or create a label"
              />
            </div>
            {/* The app's scrollbar, not the browser's (owner, 13.10.2026). */}
            <OsScroll style={{ maxHeight: '16rem', minHeight: 0 }}>
            <div className="auth-picker__list auth-picker__list--os">
              {shown.map((l) => {
                const on = held.includes(l.id);
                const c = labelColor(l.color);
                return (
                  <label key={l.id} className={`auth-picker__row task-label-row${on ? ' auth-picker__row--active' : ''}`} role="option" aria-selected={on}>
                    <input type="checkbox" className="checkbox-brand" checked={on} onChange={() => onToggle(l.id)} aria-label={l.name} />
                    <span className="task-label-swatch" style={{ '--label-bg': c.bg, '--label-bg-dark': c.darkBg }} aria-hidden />
                    <span className="min-w-0 flex-1 truncate text-left">{l.name}</span>
                  </label>
                );
              })}
              {!shown.length && !q && <div className="auth-picker__empty">No labels on this board yet — type one above</div>}
            </div>
            </OsScroll>
            {q && !exact && onCreate && (
              <div className="auth-picker__footer">
                <button type="button" className="auth-picker__row task-label-create" onClick={create}>
                  <Plus size={13} aria-hidden />
                  <span className="min-w-0 flex-1 truncate text-left">Create “{q}”</span>
                </button>
              </div>
            )}
          </div>
        </>,
        document.body
      )
    : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`task-chip${held.length ? '' : ' task-chip--ghost'}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Labels"
        onClick={() => (open ? close() : setOpen(true))}
      >
        <Tag size={12} aria-hidden />
        {held.length ? `Labels · ${held.length}` : 'Labels'}
        <ChevronDown size={12} aria-hidden />
      </button>
      {menu}
    </>
  );
}
